import { EventReward, PassLevel, PassTrack, RolloverSummary, SeasonTheme, gemPrices, premiumItems, seasonAt, seasonEndsAt, seasonStartsAt, seasonTheme, showsValue, tierProgress, valuePercent, vipXpForPrice } from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import type { Season } from "./season";

/**
 * The Pass sheet's view of the season (MP-22): the season's name and countdown, the tier bar, every tier's free and
 * premium cell with its state, and the two purchase cards with their honest value and VIP XP. Built from the use cases
 * (season.ts) and the rules (domain meta/season.ts); the web draws it and decides nothing.
 */

/** A cell's state: claimed; reached and waiting (CLAIM); reached but premium not bought (a lock); not reached yet. */
export type PassCellState = "claimed" | "ready" | "locked" | "future";

export interface PassCell {
  readonly track: PassTrack;
  readonly tier: number;
  readonly reward: EventReward;
  readonly state: PassCellState;
  /** The tier that pays the season's cosmetic (title / skin). */
  readonly cosmetic: boolean;
}

export interface PassRow {
  readonly tier: number;
  readonly reached: boolean;
  /** Null on tiers the free track skips. */
  readonly free: PassCell | null;
  readonly premium: PassCell | null;
}

export interface PassProductView {
  readonly level: Exclude<PassLevel, "free">;
  readonly product: string;
  readonly name: string;
  /** The reference price ("$9.99"); the screen shows the store's own when it has it. */
  readonly priceLabel: string;
  readonly valuePercent: number;
  readonly showValue: boolean;
  readonly vipPoints: number;
  /** Tiers Premium+ adds at once (0 for Premium). */
  readonly tierSkip: number;
  /** "" when it can be bought now; else why not ("already owned"). */
  readonly blocked: string;
}

export interface SeasonView {
  /** A season runs now (else `startsAt` is the first one's start). */
  readonly live: boolean;
  readonly open: boolean;
  readonly unlockAfterLevel: number;
  /** 0-based; shown as `number`. */
  readonly season: number;
  readonly number: number;
  readonly theme: SeasonTheme;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly xp: number;
  readonly tier: number;
  readonly tiers: number;
  readonly into: number;
  readonly need: number;
  readonly maxed: boolean;
  readonly pass: PassLevel;
  readonly rows: readonly PassRow[];
  readonly claimable: number;
  readonly premium: PassProductView;
  readonly plus: PassProductView;
  readonly summary: RolloverSummary | null;
  /** XP per source, for the "how to earn" line. */
  readonly xpRules: Readonly<Record<"win" | "loss" | "order" | "eventNode" | "dailyRoad", number>>;
}

export function seasonView(catalog: LevelCatalog, season: Season, deviceNow: number): SeasonView {
  season.sync(deviceNow);
  const d = season.data, s = season.state, now = season.now(deviceNow);
  const live = season.live(deviceNow);
  const n = live ? s.season : Math.max(0, seasonAt(d, now) + 1);
  const p = tierProgress(d, s.xp);
  const cell = (track: PassTrack, tier: number): PassCell | null => {
    const def = (track === "free" ? d.free : d.premiumTrack).find((r) => r.tier === tier);
    if (!def) return null;
    const reached = live && s.tier >= tier;
    const state: PassCellState = s.claimed(track, tier) ? "claimed" : !reached ? "future" : track === "premium" && !s.premiumOwned ? "locked" : "ready";
    return { track, tier, reward: def.reward, state, cosmetic: def.cosmetic };
  };
  const rows: PassRow[] = Array.from({ length: d.tiers }, (_, i) => ({ tier: i + 1, reached: live && s.tier >= i + 1, free: cell("free", i + 1), premium: cell("premium", i + 1) }));
  const items = premiumItems(d), prices = gemPrices(catalog.balance);
  const product = (level: "premium" | "plus"): PassProductView => {
    const def = level === "plus" ? d.premiumPlus : d.premium;
    const percent = valuePercent(catalog.balance, items, def.price.cents, prices);
    const blocked = season.prepare(def.price.product, deviceNow);
    return {
      level, product: def.price.product, name: def.name, priceLabel: def.price.label, valuePercent: percent, showValue: showsValue(catalog.balance, percent),
      vipPoints: vipXpForPrice(catalog.balance.vip, def.price.cents), tierSkip: level === "plus" ? d.premiumPlus.tierSkip : 0, blocked: blocked === true ? "" : blocked,
    };
  };
  return {
    live, open: season.open, unlockAfterLevel: d.unlockAfterLevel, season: n, number: n + 1, theme: seasonTheme(d, n),
    startsAt: seasonStartsAt(d, n), endsAt: seasonEndsAt(d, n), xp: s.xp, tier: live ? s.tier : 0, tiers: d.tiers, into: p.into, need: p.need, maxed: p.maxed,
    pass: live ? s.pass : "free", rows, claimable: live && season.open ? s.claimable(d).length : 0, premium: product("premium"), plus: product("plus"),
    summary: s.summary, xpRules: d.xp,
  };
}
